import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import { CheckCircle2, Upload } from 'lucide-react';
import { useServiceDesksWithRequestTypes } from '../../../hooks/useServiceDesksWithRequestTypes';
import { useApplyQuotaCSV, useValidateQuotaCSV } from '../../../hooks/useQuotaCsvImport';
import type { QuotaImportDiff, QuotaImportEntry } from '../../../api/quotasApi';

interface QuotaCSVImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

type Step = 'upload' | 'validating' | 'preview' | 'importing' | 'complete';

export const QuotaCSVImportDialog = ({ open, onClose, onImported }: QuotaCSVImportDialogProps) => {
  const { t } = useTranslation();
  const { serviceDesks } = useServiceDesksWithRequestTypes(open);

  const [step, setStep] = useState<Step>('upload');
  const [serviceDeskId, setServiceDeskId] = useState('');
  const [requestTypeId, setRequestTypeId] = useState('');
  const [disableMissing, setDisableMissing] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [diff, setDiff] = useState<QuotaImportDiff | null>(null);

  const { mutate: validateCSV, error: validateError, reset: resetValidate } = useValidateQuotaCSV({
    onSuccess: (result) => {
      setDiff(result);
      setStep('preview');
    },
    onError: () => setStep(diff ? 'preview' : 'upload'),
  });

  const { mutate: applyCSV, error: applyError, reset: resetApply } = useApplyQuotaCSV({
    onSuccess: ({ diff: applied, applied: written }) => {
      setDiff(applied);
      setStep(written ? 'complete' : 'preview');
      if (written) onImported();
    },
    onError: () => setStep('preview'),
  });

  const requestTypes = serviceDesks.find((sd) => String(sd.id) === serviceDeskId)?.requestTypes ?? [];
  const scopeReady = Boolean(serviceDeskId && requestTypeId);
  const error = (validateError ?? applyError) as Error | null;

  const scope = (archiveMissing: boolean) => ({
    serviceDeskId: Number(serviceDeskId),
    requestTypeId: Number(requestTypeId),
    disableMissing: archiveMissing,
  });

  const reset = () => {
    setStep('upload');
    setFile(null);
    setDiff(null);
    resetValidate();
    resetApply();
  };

  const handleClose = () => {
    reset();
    setDisableMissing(false);
    onClose();
  };

  const preview = (nextFile: File, archiveMissing: boolean) => {
    resetApply();
    setStep('validating');
    validateCSV({ file: nextFile, scope: scope(archiveMissing) });
  };

  const handleFile = (nextFile: File) => {
    setFile(nextFile);
    preview(nextFile, disableMissing);
  };

  /** Archiving changes what the diff contains, so the preview is recomputed, never patched. */
  const handleArchiveToggle = (checked: boolean) => {
    setDisableMissing(checked);
    if (file) preview(file, checked);
  };

  const handleImport = () => {
    if (!file) return;
    setStep('importing');
    applyCSV({ file, scope: scope(disableMissing) });
  };

  const renderScope = () => (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="importServiceDeskId">{t('Service Desk *')}</Label>
        <select
          id="importServiceDeskId"
          value={serviceDeskId}
          onChange={(e) => {
            setServiceDeskId(e.target.value);
            setRequestTypeId('');
          }}
          className="w-full px-3 py-2 border rounded-md text-sm"
        >
          <option value="">{t('Select a service desk…')}</option>
          {serviceDesks.map((sd) => (
            <option key={sd.id} value={sd.id}>
              {sd.name} ({sd.projectKey})
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="importRequestTypeId">{t('Request type *')}</Label>
        <select
          id="importRequestTypeId"
          value={requestTypeId}
          onChange={(e) => setRequestTypeId(e.target.value)}
          className="w-full px-3 py-2 border rounded-md text-sm"
          disabled={!serviceDeskId}
        >
          <option value="">{t('Select a request type…')}</option>
          {requestTypes.map((rt) => (
            <option key={rt.id} value={rt.id}>
              {rt.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderUpload = () => (
    <div className="space-y-4">
      {renderScope()}
      <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary transition-colors">
        <Upload className="mx-auto h-10 w-10 text-gray-400 mb-3" />
        <p className="text-sm text-gray-600 mb-3">
          {t('Sectors in rows, brands in columns, the limit in each cell')}
        </p>
        <label className={`inline-block ${scopeReady ? '' : 'pointer-events-none opacity-50'}`}>
          <input
            type="file"
            aria-label={t('Browse')}
            accept=".csv,text/csv"
            className="hidden"
            disabled={!scopeReady}
            onChange={(e) => {
              const selected = e.target.files?.[0];
              if (selected) handleFile(selected);
            }}
          />
          <Button type="button" variant="outline" asChild>
            <span>{t('Browse')}</span>
          </Button>
        </label>
        {!scopeReady && (
          <p className="text-xs text-muted-foreground mt-3">
            {t('The import writes on the scope picked above, never on your own service desk')}
          </p>
        )}
      </div>
      {error && <p className="text-sm text-red-600">{error.message}</p>}
    </div>
  );

  const renderSpinner = (message: string) => (
    <div className="py-8 text-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3" />
      <p className="text-sm text-gray-600">{message}</p>
    </div>
  );

  const renderCounts = (counts: { label: string; value: number; tone: string }[]) => (
    <div className="grid grid-cols-5 gap-2 text-sm text-center">
      {counts.map(({ label, value, tone }) => (
        <div key={label} className={`border rounded p-2 ${tone}`}>
          <p className="font-medium">{value}</p>
          <p className="text-xs">{label}</p>
        </div>
      ))}
    </div>
  );

  /**
   * Every pair a bucket will write, listed in full — a recap the user confirms from, not a count.
   * `from` is present on a limit change, absent on a creation or a same-limit reactivation.
   */
  const renderSection = (label: string, entries: QuotaImportEntry[], tone: string) => {
    if (!entries.length) return null;

    return (
      <div className="border rounded">
        <p className={`text-xs font-medium px-3 py-2 border-b bg-gray-50 ${tone}`}>
          {label} ({entries.length})
        </p>
        <ul className="max-h-48 overflow-y-auto divide-y text-xs text-gray-700">
          {entries.map((entry) => (
            <li key={entry.id ?? `${entry.sector}-${entry.brand}`} className="px-3 py-1.5">
              <span className="font-medium">
                {entry.sector} / {entry.brand}
              </span>{' '}
              : {entry.from !== undefined ? `${entry.from} → ${entry.limit}` : entry.limit}
              {(entry.sheetSector || entry.sheetBrand) && (
                <span className="text-amber-600">
                  {' '}
                  {t('(grid: {{value}})', {
                    value: `${entry.sheetSector ?? entry.sector} / ${entry.sheetBrand ?? entry.brand}`,
                  })}
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderPreview = () => {
    if (!diff) return null;
    const { existingCount, unverified, duplicates, toCreate, toUpdate, toReactivate, toArchive, unchanged, errors } = diff;
    const unknown = [...unverified.sectors, ...unverified.brands];
    const writes = toCreate.length + toUpdate.length + toReactivate.length + toArchive.length;
    const deskName = serviceDesks.find((sd) => String(sd.id) === serviceDeskId)?.name ?? serviceDeskId;
    const typeName = requestTypes.find((rt) => String(rt.id) === requestTypeId)?.name ?? requestTypeId;

    return (
      <div className="space-y-4 min-w-0 max-w-full">
        {/* An empty scope is the usual cause of a wall of "introuvable": say so before the errors. */}
        <div
          className={`text-xs rounded border px-3 py-2 ${
            existingCount === 0 ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-gray-50 text-gray-600'
          }`}
        >
          {t('Scope: {{desk}} / {{type}} — {{count}} existing quota', {
            desk: deskName,
            type: typeName,
            count: existingCount,
          })}
          {existingCount === 0 && (
            <span className="block mt-1 font-medium">
              {t('No quota on this scope yet: every pair in the grid will be created. Check the request type is the one you meant.')}
            </span>
          )}
        </div>
        {renderCounts([
          { label: t('created'), value: toCreate.length, tone: 'bg-green-50 border-green-200 text-green-700' },
          { label: t('updated'), value: toUpdate.length, tone: 'bg-blue-50 border-blue-200 text-blue-700' },
          { label: t('reactivated'), value: toReactivate.length, tone: 'bg-indigo-50 border-indigo-200 text-indigo-700' },
          { label: t('unchanged'), value: unchanged.length, tone: 'bg-gray-50 text-gray-700' },
          { label: t('archived'), value: toArchive.length, tone: 'bg-orange-50 border-orange-200 text-orange-700' },
        ])}

        {errors.length > 0 && (
          <div className="max-h-40 overflow-y-auto border rounded p-3 bg-red-50">
            <p className="text-xs font-medium text-red-700 mb-2">{t('Nothing will be imported')}</p>
            <ul className="space-y-1 text-xs text-red-600">
              {errors.slice(0, 20).map((e, index) => (
                <li key={`${e.line}-${e.column ?? ''}-${index}`}>
                  {e.line > 0 ? `${t('Line')} ${e.line}` : t('File')}
                  {e.column ? ` — ${e.column}` : ''} : {e.message}
                </li>
              ))}
              {errors.length > 20 && <li>{t('… and {{count}} more', { count: errors.length - 20 })}</li>}
            </ul>
          </div>
        )}

        {/* Not an error: the manual dialog writes free text too. But a typo counts zero for ever,
            and the recap is the only place it can still be caught. */}
        {unknown.length > 0 && (
          <div className="text-xs rounded border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2">
            <p className="font-medium">
              {t('{{count}} value is used by no existing quota and will be written as spelled', {
                count: unknown.length,
              })}
            </p>
            <p className="mt-1">{unknown.join(' · ')}</p>
            <p className="mt-1">
              {t('Check the spelling matches Jira exactly — otherwise the quota counts zero and never blocks.')}
            </p>
          </div>
        )}

        {duplicates.length > 0 && (
          <div className="text-xs rounded border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2">
            <p className="font-medium">
              {t('{{count}} pair already carries several rules; only the first is updated', {
                count: duplicates.length,
              })}
            </p>
            <p className="mt-1">{duplicates.join(' · ')}</p>
          </div>
        )}

        {errors.length === 0 && (
          <div className="space-y-3">
            {renderSection(t('Quotas to create'), toCreate, 'text-green-700')}
            {renderSection(t('Quotas to update'), toUpdate, 'text-blue-700')}
            {renderSection(t('Quotas to reactivate'), toReactivate, 'text-indigo-700')}
            {renderSection(t('Quotas to archive'), toArchive, 'text-orange-700')}
          </div>
        )}

        <div className="flex items-center gap-2">
          <Checkbox
            id="disableMissing"
            checked={disableMissing}
            onCheckedChange={(checked) => handleArchiveToggle(checked === true)}
          />
          <Label htmlFor="disableMissing" className="text-sm font-normal">
            {t('Archive the rules absent from the grid')}
          </Label>
        </div>

        {error && <p className="text-sm text-red-600">{error.message}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={reset}>
            {t('Restart')}
          </Button>
          <Button onClick={handleImport} disabled={errors.length > 0 || writes === 0}>
            {t('Apply {{count}} change', { count: writes })}
          </Button>
        </div>
      </div>
    );
  };

  const renderComplete = () => (
    <div className="py-6 text-center space-y-3">
      <CheckCircle2 className="mx-auto h-12 w-12 text-green-500" />
      <p className="text-sm text-gray-700">
        {t('{{created}} created, {{updated}} updated, {{reactivated}} reactivated, {{archived}} archived', {
          created: diff?.toCreate.length ?? 0,
          updated: diff?.toUpdate.length ?? 0,
          reactivated: diff?.toReactivate.length ?? 0,
          archived: diff?.toArchive.length ?? 0,
        })}
      </p>
      <Button onClick={handleClose}>{t('Close')}</Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-2xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t('Import a quota grid')}</DialogTitle>
          <DialogDescription>
            {step === 'upload' && t('Upload the monthly grid as a CSV')}
            {step === 'validating' && t('Reading the grid, nothing is written yet')}
            {step === 'preview' && t('Check and confirm the changes')}
            {step === 'importing' && t('Writing the quota rules')}
            {step === 'complete' && t('Done')}
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && renderUpload()}
        {step === 'validating' && renderSpinner(t('Reading the grid…'))}
        {step === 'preview' && renderPreview()}
        {step === 'importing' && renderSpinner(t('Writing the quota rules…'))}
        {step === 'complete' && renderComplete()}
      </DialogContent>
    </Dialog>
  );
};
